/* eslint-disable react/no-array-index-key */
/** @jsxImportSource @emotion/react */

import { memo, ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useLocation, useHistory } from "react-router-dom";

import {
  Card,
  CardHeader,
  CardContent,
  Drawer as DrawerComp,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  CircularProgress,
  SelectProps,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { LocationState } from "history";

import { Icon, Button } from "..";
import logoUrl from "../../assets/images/logo-moment.svg";
import config from "../../config";
import { useAccount } from "../../context/account";
import { useModal } from "../../context/modal";
import usePopupPolicy from "../../hooks/usePopupPolicy";
import { Tier } from "../../types/tier";
import { minsToDuration } from "../../utils/converters";
import dateTimeFormat from "../../utils/dateTimeFormat";
import { durationFormat } from "../../utils/parsers";
import Select from "../Select";

import {
  drawer,
  logo,
  list,
  menuGap,
  focusedListItem,
  btn,
  btnHighlight,
  btnFocused,
  icon,
  iconHighlight,
  version,
  inputAdornment,
  timeBlock,
  timeBlockHeader,
  timeBlockContent,
  accessTimeIcon,
  moreBtn,
  timeCaption,
  time,
  upgradeBtn,
  moreDetail,
  policyBtn,
} from "./styles";

interface CustomLocationState {
  pathname?: string;
}

export enum ItemType {
  NavigationLink = "link",
  DropdownSelect = "select",
}

interface NavigationLinkItem {
  icon: string;
  txt: string;
  to?: string | LocationState;
  highlight?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

interface DropdownSelectItem extends Pick<SelectProps, "value" | "onChange"> {
  icon: ReactNode;
  options: {
    value: string;
    label: string;
  }[];
}

type Item = (
  | (NavigationLinkItem & { itemType?: ItemType.NavigationLink })
  | (DropdownSelectItem & { itemType?: ItemType.DropdownSelect })
) & {
  startAtBottom?: boolean;
};

interface Props {
  items: Item[];
}

const Drawer = ({ items }: Props): JSX.Element => {
  const intl = useIntl();
  const { pathname } = useLocation();
  const { push } = useHistory();
  const { tier, availableMins, usedMins, lastPurchasedAt } = useAccount();
  const [showModal, hideModal] = useModal();
  const popupPolicyModal = usePopupPolicy();
  const topItems = items.slice(
    0,
    items.findIndex((item) => item.startAtBottom)
  );
  const bottomItems = items.slice(topItems.length);
  const isShowAvailableTime = tier !== Tier.ENTERPRISE;

  const isItemFocused = (to: unknown) => {
    if (typeof to === "string") {
      return pathname.startsWith(to);
    }
    if ((to as CustomLocationState)?.pathname) {
      const itemPathname = (to as CustomLocationState)?.pathname;
      return itemPathname ? pathname.startsWith(itemPathname) : false;
    }
    return false;
  };
  const renderItems = (listItems: Item[]): ReactNode[] =>
    listItems.map((item, i) => {
      const { itemType = ItemType.NavigationLink } = item;

      if (itemType === ItemType.NavigationLink) {
        const { icon: ic, txt, to, highlight, disabled, loading, onClick } = item as NavigationLinkItem;

        return (
          <ListItem key={`item-${i}`} css={isItemFocused(to) && !highlight && focusedListItem}>
            <Button
              css={[btn, highlight && btnHighlight, !highlight && isItemFocused(to) && btnFocused]}
              component={Link}
              to={to}
              disabled={disabled || loading}
              onClick={onClick}
            >
              <ListItemIcon css={[icon, highlight && iconHighlight]}>
                {loading ? <CircularProgress color="primary" size={16} /> : <Icon name={ic} fontSize="small" />}
              </ListItemIcon>
              <ListItemText primary={txt} />
            </Button>
          </ListItem>
        );
      }

      if (itemType === ItemType.DropdownSelect) {
        const { value, onChange, options, icon: ic } = item as DropdownSelectItem;

        return (
          <ListItem key={`item-${i}`}>
            <Select
              value={value}
              onChange={onChange}
              options={options}
              startAdornment={
                <InputAdornment position="start" css={inputAdornment}>
                  {ic}
                </InputAdornment>
              }
              IconComponent={ExpandMoreIcon}
            />
          </ListItem>
        );
      }

      return null;
    });

  const popupOverChargePolicyModal = () => {
    popupPolicyModal({ onOK: () => popupMoreDetailModal() });
  };

  const popupMoreDetailModal = () => {
    showModal({
      title: intl.formatMessage({ id: "popout-title.more-detail" }),
      content: (
        <div css={moreDetail}>
          <Typography variant="body2">
            {intl.formatMessage(
              {
                id: "popout-context.more-detail.available-time",
              },
              { time: durationFormat(minsToDuration(availableMins), intl) }
            )}
          </Typography>
          <Typography variant="body2">
            {intl.formatMessage(
              {
                id: "popout-context.more-detail.used-time",
              },
              { time: durationFormat(minsToDuration(usedMins), intl) }
            )}
          </Typography>
          <Typography variant="body2">
            {intl.formatMessage(
              {
                id: "popout-context.more-detail.last-purchased-date",
              },
              {
                date: lastPurchasedAt
                  ? dateTimeFormat(lastPurchasedAt, intl, {
                      timeConfig: {
                        hourCycle: "h23",
                        hour: "numeric",
                        minute: "numeric",
                        second: "numeric",
                      },
                    })
                  : "- -",
              }
            )}
          </Typography>
          <Button color="primary" css={policyBtn} onClick={() => popupOverChargePolicyModal()}>
            {intl.formatMessage({
              id: "popout-context.more-detail.over-charge-policy",
            })}
          </Button>
        </div>
      ),
      actions: [
        {
          primary: true,
          label: intl.formatMessage({ id: "btn.ok" }),
          onClick: () => hideModal(),
        },
      ],
    });
  };

  const renderAvailableTime = (): JSX.Element => (
    <ListItem key="item-available-time">
      <Card css={timeBlock}>
        <CardHeader
          disableTypography
          css={timeBlockHeader}
          title={<Icon name="access-time" css={accessTimeIcon} />}
          action={
            <Button color="primary" css={moreBtn} onClick={() => popupMoreDetailModal()}>
              {intl.formatMessage({ id: "btn.more-detail" })}
            </Button>
          }
        />
        <CardContent css={timeBlockContent}>
          <Typography variant="caption" css={timeCaption}>
            {intl.formatMessage({ id: "available-time" })}
          </Typography>
          <Typography css={time}>{durationFormat(minsToDuration(availableMins), intl)}</Typography>
          <Button
            variant="contained"
            color="primary"
            css={upgradeBtn}
            onClick={() => push("/pricing", { from: pathname })}
          >
            {intl.formatMessage({ id: "btn.upgrade-plan" })}
          </Button>
        </CardContent>
      </Card>
    </ListItem>
  );

  return (
    <DrawerComp css={drawer} variant="permanent" anchor="left">
      <Link to="/">
        <img css={logo} src={logoUrl} alt="KKStream Live" />
      </Link>
      <List css={list}>
        {renderItems(topItems)}
        <div css={menuGap} />
        {isShowAvailableTime && renderAvailableTime()}
        {renderItems(bottomItems)}
      </List>
      <Typography css={version} variant="caption" component="div">
        <FormattedMessage id="live.drawer.version" values={{ version: `${config.VERSION}.${config.BUILD}` }} />
      </Typography>
    </DrawerComp>
  );
};

export default memo(Drawer);
